import React, { useState } from 'react';
import { ArrowLeft, MapPin, Building2, ChevronDown } from 'lucide-react';
import { COUNTRIES } from '../../utils/countries';

interface CompanyStep2LocationsProps {
    companyLocation: string;
    hiringLocation: string;
    onUpdate: (companyLoc: string, hiringLoc: string) => void;
    onBack: () => void;
    onComplete: () => void;
}


export const CompanyStep2Locations: React.FC<CompanyStep2LocationsProps> = ({
    companyLocation,
    hiringLocation,
    onUpdate,
    onBack,
    onComplete
}) => {
    const [localCompanyLocation, setLocalCompanyLocation] = useState(companyLocation);
    const [localHiringLocation, setLocalHiringLocation] = useState(hiringLocation);
    const [error, setError] = useState('');

    const handleContinue = () => {
        if (!localCompanyLocation.trim() || !localHiringLocation.trim()) {
            setError('Please select both locations');
            return;
        }
        setError('');
        onUpdate(localCompanyLocation, localHiringLocation);
        onComplete();
    };

    const isValid = localCompanyLocation.trim().length > 0 && localHiringLocation.trim().length > 0;

    return (
        <div className="w-full max-w-md mx-auto">
            <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-3">Location Details</h1>
            <p className="text-sm md:text-base text-gray-600 mb-6 md:mb-8">
                Tell us where your company is based and where you're hiring.
            </p>

            <div className="space-y-4 md:space-y-6">
                {/* Company Location */}
                <div>
                    <label htmlFor="companyLocation" className="flex items-center gap-2 text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                        <Building2 className="w-3.5 h-3.5 md:w-4 md:h-4" />
                        Company Location <span className="text-red-500">*</span>
                    </label>
                    <p className="text-[10px] md:text-xs text-gray-500 mb-2">
                        Where is your company headquartered or primarily based?
                    </p>
                    <div className="relative">
                        <select
                            id="companyLocation"
                            value={localCompanyLocation}
                            onChange={(e) => {
                                setLocalCompanyLocation(e.target.value);
                                setError('');
                            }}
                            className={`w-full px-3 md:px-4 py-2.5 md:py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors appearance-none bg-white text-sm md:text-base ${error && !localCompanyLocation.trim()
                                ? 'border-red-500 focus:ring-red-200'
                                : 'border-gray-300 focus:ring-slate-200 focus:border-slate-900'
                                }`}
                        >
                            <option value="">Select a country...</option>
                            {COUNTRIES.map((country) => (
                                <option key={country} value={country}>
                                    {country}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                {/* Hiring Location */}
                <div>
                    <label htmlFor="hiringLocation" className="flex items-center gap-2 text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                        <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4" />
                        Hiring Location <span className="text-red-500">*</span>
                    </label>
                    <p className="text-[10px] md:text-xs text-gray-500 mb-2">
                        Where are you hiring? (Can be same as company location or different)
                    </p>
                    <div className="relative">
                        <select
                            id="hiringLocation"
                            value={localHiringLocation}
                            onChange={(e) => {
                                setLocalHiringLocation(e.target.value);
                                setError('');
                            }}
                            className={`w-full px-3 md:px-4 py-2.5 md:py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors appearance-none bg-white text-sm md:text-base ${error && !localHiringLocation.trim()
                                ? 'border-red-500 focus:ring-red-200'
                                : 'border-gray-300 focus:ring-slate-200 focus:border-slate-900'
                                }`}
                        >
                            <option value="">Select a country...</option>
                            {COUNTRIES.map((country) => (
                                <option key={country} value={country}>
                                    {country}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                {error && <p className="text-xs md:text-sm text-red-600 text-center">{error}</p>}

                {/* Buttons */}
                <div className="flex flex-col-reverse sm:flex-row gap-3 md:gap-4 pt-2 md:pt-4">
                    <button
                        onClick={onBack}
                        className="flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 md:py-3 text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm md:text-base"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                    <button
                        onClick={handleContinue}
                        disabled={!isValid}
                        className="flex-1 px-4 md:px-6 py-2.5 md:py-3 bg-black text-white rounded-lg font-medium hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                    >
                        Complete Setup
                    </button>
                </div>
            </div>
        </div>
    );
};
